import { useMemo, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Stack,
  Typography,
  TextField,
  Chip
} from '@mui/material'
import { gql, useQuery } from '@apollo/client'
import CircleIcon from '@mui/icons-material/Circle'
import { useNavigate } from 'react-router-dom'

import useFocus from 'components/AuthenticatedLayout/Header/AccountsPerimeter/use-focus'
import { availableToColor, LIST_UP_COLOR } from 'pages/Supervision/helpers'
import { Site } from '__generated__/graphql'
import useBuildings from '../use-buildings'
import OverflowTip from 'components/OverflowTip'

const OPERATION_CHILDREN_ACCOUNT = gql`
  query GetChildrenAccount($sf_id: String) {
    accounts: childrenAccount(sf_id: $sf_id) {
      sf_id
    }
  }
`

const OPERATION_MONITORING_SITES = gql`
  query GetAccountsMetrics($sf_ids: [String!]) {
    sites: monitoringSites(sf_ids: $sf_ids) {
      site {
        id
        name
        sf_id          
      }
      available_level
    }
  }
`

const useSites = ({ site }) => {
  const { data: dataAccounts } = useQuery(OPERATION_CHILDREN_ACCOUNT, {
    variables: {
      sf_id: site.account_parent.sf_id
    }
  })
  const { data: dataSites } = useQuery(OPERATION_MONITORING_SITES, {
    variables: {
      sf_ids: (dataAccounts?.accounts || []).map(({ sf_id }) => sf_id),
      skip: dataAccounts?.accounts?.length
    }
  })
  const sites = useMemo(() => {
    return (dataSites?.sites || []).map(({ site, available_level }) => ({
      id: site.sf_id,
      name: site.name,
      metrics: { available_level }
    }))
  }, [dataSites])
  return sites
}

const NavigateToSite = ({ focusId, onRedirect }) => {
  const { loading, redirect } = useBuildings(focusId)

  useEffect(() => {
    if (!loading) {
      onRedirect(redirect())
    }
  }, [loading])

  return null
}

const AccountPanelTreeAutocomplete = ({
  selectedSite
}: { selectedSite: Site }) => {
  const [focusId, setFocusId] = useState<string | null | undefined>(null)
  const navigate = useNavigate()
  const {onFocus} = useFocus()
  const sites = useSites({ site: selectedSite })
  return (
    <>
      <Autocomplete
        multiple
        sx={{
          width: '100%',
          '& .MuiInputBase-root': {
            flexWrap: 'nowrap',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          },
          '& .MuiAutocomplete-tag': {
            flexShrink: 0,
          },
          '& .MuiAutocomplete-input': {
            flexGrow: 1,
            minWidth: '100px',
          },
          '& .MuiFormControl-root': {
            padding: 0
          },
        }}
        fullWidth
        disablePortal
        disableClearable
        options={sites}
        value={[selectedSite]}
        isOptionEqualToValue={(option, value) => option?.id === value?.sf_id || option?.id === value?.id}
        onChange={(e, values) => {
          if (!values.length) {
            return
          }
          setFocusId(values.pop()?.id)
        }}
        getOptionLabel={(o) => o?.name ?? ''}
        renderOption={(props, option) => (
          <Box component={'li'} {...props}>
            <Stack alignItems="center" direction="row" gap={1}>
              <CircleIcon
                sx={{
                  fontSize: '14px',
                  color: option?.metrics?.available_level === 'up' ? LIST_UP_COLOR : availableToColor(option?.metrics?.available_level),
                }}
              />
              <Typography sx={{ fontSize: '12px' }}>{option?.name}</Typography>
            </Stack>
          </Box>
        )}
        renderTags={(value, getTagProps) => {
          return value.map((option, index: number) => (
            <Chip
              {...getTagProps({ index })}
              key={option?.id}
              label={option?.name}
              icon={
                <CircleIcon
                  sx={{
                    verticalAlign: 'sub',
                    '&&': {
                      fontSize: '14px',
                      color: `${option?.metrics?.available_level === 'up' ? LIST_UP_COLOR : availableToColor(
                        option?.metrics?.available_level,
                      )} !important`,
                    },
                  }}
                />
              }
              disabled={false}
              onDelete={undefined}
              component={({ children }) => {
                return (
                  <Box
                    sx={{
                      minHeight: '32px',
                      minWidth: '130px',
                      maxWidth: '175px',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '12px',
                      lineHeight: '18px',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      pl: '4px',
                      borderRight: 'solid 1px',
                      borderColor: 'border.primary'
                    }}
                  >
                    <OverflowTip>{children}</OverflowTip>
                  </Box>
                )
              }}
              sx={{
                borderRadius: 0,
                bgcolor: 'transparent',
                borderRight: '1px solid',
                borderRightColor: 'border.primary',
                fontSize: '12px',
              }}
            />
          ))
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              multiline={false}
              sx={{
                bgcolor: 'transparent',
                padding: '0 8px',
                '& .MuiInputBase-root': {
                  border: 'solid 1px !important',
                  borderColor: '#E7E7E7 !important',
                  py: '3px !important',
                  '&:hover': {
                    borderColor: '#3B82F6 !important',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiInputBase-root.Mui-focused': {
                  boxShadow: 'none',
                },
              }}
            />
          )
        }}
      />
      {
        focusId && (
          <NavigateToSite
            focusId={focusId}
            onRedirect={url => {
              setFocusId(null)
              onFocus({sf_id: focusId})
              navigate(({ pathname: url }))
            }}
          />
        )
      }
    </>
  )
}

export default AccountPanelTreeAutocomplete
